import React, { useState } from 'react';
import { useApi, api } from '../hooks/useApi';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

const TYPES = [
  { value:'bug',        label:'🐛 Feilmelding' },
  { value:'suggestion', label:'💡 Forslag til forbedring' },
  { value:'question',   label:'❓ Spørsmål' },
  { value:'urgent',     label:'🆘 Akutt problem' },
];

const STATUS_LABELS = { open:'Åpen', in_progress:'Under behandling', resolved:'Løst', closed:'Lukket' };
const STATUS_BG     = { open:'var(--yellow-bg)', in_progress:'var(--blue-bg)', resolved:'var(--green-bg)', closed:'var(--bg2)' };
const STATUS_COLOR  = { open:'var(--yellow)', in_progress:'var(--blue)', resolved:'var(--green)', closed:'var(--txm)' };
const STATUS_ICON   = { open:'🟡', in_progress:'🔵', resolved:'🟢', closed:'⚫' };

const EMPTY = { type:'bug', title:'', description:'', priority:'normal' };

export default function SupportPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { data: tickets, refresh } = useApi('/api/support');
  const [form, setForm]         = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sending, setSending]   = useState(false);
  const [toast, setToast]       = useState('');

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const set = k => e => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async e => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSending(true);
    try {
      await api.post('/api/support', form);
      notify('Saken er sendt inn!');
      setForm(EMPTY);
      setShowForm(false);
      refresh();
    } catch (ex) {
      notify('Noe gikk galt — prøv igjen');
    } finally { setSending(false); }
  };

  const list = tickets || [];

  const renderForm = () => (
    <div className="glass-card" style={{ margin: isMobile ? '0 14px 12px' : '0 0 12px', padding:16 }}>
      <div style={{ fontSize:15, fontWeight:800, color:txt, marginBottom:14 }}>Ny sak</div>
      <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          <label style={lbl}>Type</label>
          <select style={inp(dark)} value={form.type} onChange={set('type')}>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          <label style={lbl}>Tittel *</label>
          <input style={inp(dark)} value={form.title} onChange={set('title')} required placeholder="Kort beskrivelse" autoFocus />
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          <label style={lbl}>Beskrivelse *</label>
          <textarea style={{...inp(dark), resize:'vertical', minHeight:90}} value={form.description}
            onChange={set('description')} required placeholder="Beskriv problemet eller forslaget…" />
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          <label style={lbl}>Prioritet</label>
          <div style={{ display:'flex', gap:8 }}>
            {[['low','Lav'],['normal','Normal'],['high','Høy']].map(([v,l]) => (
              <button key={v} type="button"
                style={{ flex:1, padding:'8px', borderRadius:9, border:'1px solid var(--bdr)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit',
                  background: form.priority===v ? '#7C6FF7' : 'var(--bg)',
                  color:      form.priority===v ? '#fff'    : 'var(--txm)',
                  borderColor:form.priority===v ? '#7C6FF7' : 'var(--bdr)',
                }}
                onClick={() => setForm(f => ({...f, priority:v}))}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button type="submit" disabled={sending}
            style={{ flex:1, padding:'12px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit', opacity:sending?0.7:1 }}>
            {sending ? 'Sender…' : 'Send inn'}
          </button>
          <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY); }}
            style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:14, fontFamily:'inherit' }}>
            Avbryt
          </button>
        </div>
      </form>
    </div>
  );

  const renderTickets = () => list.map(t => (
    <div key={t.id} className="glass-card" style={{ margin: isMobile ? '0 14px 8px' : '0 0 8px', padding:0, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'13px 16px', cursor:'pointer', userSelect:'none' }}
        onClick={() => setExpanded(e => e===t.id ? null : t.id)}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
            <span style={{ fontSize:14, fontWeight:700, color:txt }}>{t.title}</span>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, flexShrink:0,
              background:STATUS_BG[t.status]||'var(--bg2)', color:STATUS_COLOR[t.status]||'var(--txm)' }}>
              {STATUS_ICON[t.status]} {STATUS_LABELS[t.status]||t.status}
            </span>
          </div>
          <div style={{ fontSize:12, color:sub }}>
            {TYPES.find(x=>x.value===t.type)?.label||t.type}
            {' · '}{new Date(t.created_at).toLocaleDateString('nb-NO',{day:'numeric',month:'short',year:'numeric'})}
          </div>
        </div>
        <span style={{ fontSize:11, color:sub, flexShrink:0 }}>{expanded===t.id ? '▲' : '▼'}</span>
      </div>
      {expanded === t.id && (
        <div style={{ borderTop:`1px solid ${border}`, padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ fontSize:14, color:'var(--txm)', lineHeight:1.6, background:'var(--bg)', borderRadius:8, padding:'10px 12px' }}>{t.description}</div>
          {t.admin_response && (
            <div style={{ background:'var(--green-bg)', borderRadius:10, padding:'10px 14px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--green)', marginBottom:4 }}>✉️ Svar fra support</div>
              <div style={{ fontSize:14, color:'var(--green)', lineHeight:1.6 }}>{t.admin_response}</div>
            </div>
          )}
          {!t.admin_response && t.status === 'open' && (
            <div style={{ fontSize:12, color:'var(--txm)', fontStyle:'italic' }}>⏳ Saken er mottatt og vil bli behandlet snart</div>
          )}
        </div>
      )}
    </div>
  ));

  // ── MOBIL ─────────────────────────────────────────────────────
  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {/* Gradient hero */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding:'48px 16px 20px', position:'relative', overflow:'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Support</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>Spørsmål, feilmeldinger og forslag</div>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)}
              style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
              + Ny sak
            </button>
          )}
        </div>
        {list.length > 0 && (
          <div style={{ position:'relative', zIndex:1, marginTop:14, display:'flex', gap:10 }}>
            <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:10, padding:'8px 14px', textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{list.length}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>Saker</div>
            </div>
            <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:10, padding:'8px 14px', textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{list.filter(t=>t.status==='open').length}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>Åpne</div>
            </div>
            <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:10, padding:'8px 14px', textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{list.filter(t=>t.status==='resolved').length}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>Løst</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding:'12px 0 24px' }}>
        {toast && <div style={{ margin:'0 14px 10px', padding:'10px 14px', borderRadius:10, background:'var(--green-bg)', color:'var(--green)', fontSize:13, fontWeight:600 }}>{toast}</div>}

        {showForm && renderForm()}

        {!showForm && (
          <div style={{ padding:'0 14px 8px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:sub, letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>Mine saker</div>
          </div>
        )}

        {list.length === 0 && !showForm && (
          <div className="glass-card" style={{ margin:'0 14px', padding:'36px 20px', textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🎫</div>
            <div style={{ fontSize:14, color:sub }}>Du har ingen innsendte saker ennå</div>
          </div>
        )}

        {renderTickets()}
      </div>
    </div>
  );

  // ── DESKTOP ───────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {toast && <div style={{ background:'var(--green-bg)', color:'var(--green)', padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:600 }}>{toast}</div>}

      <div style={{ display:'flex', alignItems:'center', gap:14, background:'var(--card)', borderRadius:16, padding:'16px 18px', border:'1px solid var(--bdr)' }}>
        <div style={{ fontSize:36, flexShrink:0 }}>🎫</div>
        <div>
          <div style={{ fontSize:18, fontWeight:800, color:'var(--txt)' }}>Kontakt &amp; Support</div>
          <div style={{ fontSize:13, color:'var(--txl)', marginTop:2 }}>Send inn spørsmål, feilmeldinger eller forslag til forbedring</div>
        </div>
      </div>

      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          style={{ padding:'13px', borderRadius:12, border:'2px dashed #7C6FF7', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:15, fontFamily:'inherit', textAlign:'center' }}>
          + Ny sak
        </button>
      ) : renderForm()}

      <div style={{ fontSize:15, fontWeight:800, color:'var(--txt)', marginTop:4 }}>Mine saker</div>

      {list.length === 0 && (
        <div style={{ background:'var(--card)', borderRadius:16, padding:'36px 20px', border:'1px solid var(--bdr)', textAlign:'center', color:'var(--txl)', fontSize:14 }}>
          <div style={{fontSize:36, marginBottom:8}}>🎫</div>
          <div>Du har ingen innsendte saker ennå</div>
        </div>
      )}

      {renderTickets()}
    </div>
  );
}

const lbl = { fontSize:11, fontWeight:700, color:'var(--txm)', textTransform:'uppercase', letterSpacing:.4 };
const inp = (dark) => ({
  padding:'10px 12px', borderRadius:10,
  border:'1px solid var(--input-bdr)',
  fontFamily:'inherit', fontSize:14,
  color:'var(--input-txt)', outline:'none',
  background:'var(--input-bg)',
  width:'100%', boxSizing:'border-box',
});
